import {map} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, CopyText} from 'components';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {AnyParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ParticipantRole} from 'types';
import {sessionUtils} from 'utils';

import {DEFAULT_USER_SESSION_PARTICIPANT_FIELDS, UserSessionParticipant} from '../../UserSessionParticipant';
import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

import {InviteParticipantModal} from './InviteParticipantModal';

const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

export const ParticipantsSidebar: React.FC = () => {
    const {t} = useTranslation();
    const {
        session, assignmentName, canChangeAssignment, canUpdateParticipantData = false, canManageSession,
        canInviteGuest, canInviteAssistant, canRemoveGuest, canRemoveAssistant, maxGuests, maxAssistants, maxParticipants,
        assistantEnabled, clientParticipant, guestParticipants, assistantParticipants, sipActivity, isMobileSdkSession,
    } = useUserSession();
    const {
        changeAssignment, onParticipantUpdate, openInviteModal, onParticipantRemove
    } = useContext(UserSessionContext);

    const [inviteParticipantModalOpen, setInviteParticipantModalOpen] = useState(false);
    const [openedDetailsParticipant, setOpenedDetailsParticipant] = useState<AnyParticipant>(null);

    const handleInviteParticipantModalClose = useCallback(
        (confirm: boolean, data?: {participant?: AnyParticipant}[]) => {
            const participant = data?.[0]?.participant;
            if (confirm && participant) {
                setOpenedDetailsParticipant(participant);
            }
            setInviteParticipantModalOpen(false);
        }, []);

    const openSendInviteModal = useCallback((participant: AnyParticipant) => {
        openInviteModal(participant);
    }, [openInviteModal]);

    const sortedParticipants = useMemo(() => sessionUtils.sortParticipantList(session.participants), [session]);

    const totalParticipants = useMemo(
        () => [session.localParticipant, ...session.participants].length,
        [session.localParticipant, session.participants]
    );

    const canInviteGuestRole = useMemo(
        () => canInviteGuest && guestParticipants.length < maxGuests,
        [canInviteGuest, guestParticipants, maxGuests]
    );

    const canInviteAssistantRole = useMemo(
        () => assistantEnabled && canInviteAssistant && assistantParticipants.length < maxAssistants,
        [assistantEnabled, canInviteAssistant, assistantParticipants, maxAssistants]
    );

    const canInvite = useMemo(
        () => canManageSession && !!clientParticipant && !sipActivity && !isMobileSdkSession && totalParticipants < maxParticipants &&
            (canInviteGuestRole || canInviteAssistantRole),
        [canManageSession, clientParticipant, sipActivity, isMobileSdkSession, totalParticipants, maxParticipants,
            canInviteGuestRole, canInviteAssistantRole]
    );

    return (
        <SidebarContent
            view={SidebarView.Participants}
            header={
                <div className='tw-flex tw-items-center'>
                    <Icon name='id' size='lg' />
                    <div className='tw-mx-2'>{session.id}</div>
                    <CopyText text={session.id} iconOnly textInTooltip={false} />
                </div>
            }
        >
            {(canChangeAssignment || assignmentName) &&
                <div className='tw-mb-4'>
                    <div className='tw-flex tw-items-center tw-justify-between'>
                        <div className='tw-text-default-dimmed'>{t(SESSION_T_KEYS.LABELS.ASSIGNMENT)}</div>
                        {canChangeAssignment &&
                            <Icon
                                button
                                group
                                name='edit'
                                color='primary'
                                size='sm'
                                onClick={changeAssignment}
                            />}
                    </div>
                    {assignmentName && <div className='tw-mt-2'>{assignmentName}</div>}
                </div>}

            <div className='tw-flex tw-justify-between'>
                <div className='tw-text-default-dimmed'>{t(SESSION_T_KEYS.LABELS.PARTICIPANTS)}</div>
                {canInvite &&
                    <div>
                        <Icon
                            name='plus-circle'
                            color='primary'
                            button
                            onClick={() => setInviteParticipantModalOpen(true)}
                        />
                    </div>}
            </div>

            <UserSessionParticipant session={session} participant={session.localParticipant} local />
            {map(sortedParticipants, (p) => (
                <UserSessionParticipant
                    key={p.id}
                    session={session}
                    participant={p}
                    participantDetailsModalOpen={openedDetailsParticipant?.id === p.id}
                    fields={
                        !canManageSession ? {
                            ...DEFAULT_USER_SESSION_PARTICIPANT_FIELDS,
                            createClientConnection: false,
                            asyncRequestsReadonly: true,
                            joinLink: true
                        } :
                            {...DEFAULT_USER_SESSION_PARTICIPANT_FIELDS, joinLink: true}
                    }
                    canUpdate={sessionUtils.isInviteeParticipant(p) && canUpdateParticipantData && canManageSession}
                    canRemove={canManageSession && (
                        (p.role === ParticipantRole.Guest && canRemoveGuest) ||
                        (p.role === ParticipantRole.Assistant && canRemoveAssistant)
                    )}
                    onSendInvite={openSendInviteModal}
                    onUpdate={onParticipantUpdate}
                    onRemove={onParticipantRemove}
                    onParticipantDetailsModalClose={() => setOpenedDetailsParticipant(null)}
                />
            ))}

            {canInvite &&
                <InviteParticipantModal
                    open={inviteParticipantModalOpen}
                    onClose={handleInviteParticipantModalClose}
                />}

        </SidebarContent>
    );
};